/* @flow */

/**
 * @description module for promise related utility functions
 * @module promiseutils
 * @requires module:coreutils
 */
import { isNumber } from './coreutils';

declare type _settledTimedPromiseResult =
  | {
      name: string,
      tt: number,
      status: 'fulfilled',
      value: any // flowlint-line unclear-type:off
    }
  | {
      name: string,
      tt: number,
      status: 'rejected',
      reason: any // flowlint-line unclear-type:off
    };

/**
 * Generates promise which rejects after timeout
 *
 * @param {number} ms - timeout in millisecs
 * @param {string} cxt - context to be passed in reject message
 * @returns {Promise} - Promise rejects with timeout
 */
export function getTimeoutPromise(ms: number, cxt: string = ''): Promise<void> {
  if (!isNumber(ms) || ms <= 0) {
    return Promise.reject();
  }

  return new Promise((resolve, reject) => {
    setTimeout(() => {
      if (cxt.length) {
        cxt += ':';
      }
      reject(`${cxt}timeout`);
    }, ms);
  });
}

/**
 * Promise.allSettled with per-promise name and elapsed time (header bidding).
 *
 * @param {Array<{name: string, promise: Promise<*>}>} arr - Named promises
 * @returns {Promise<Array<_settledTimedPromiseResult>>} - Native settled shape plus name/tt
 */
export function allSettledTimedPromise(
  arr: Array<{ name: string, promise: Promise<any> }> // flowlint-line unclear-type:off
): Promise<Array<_settledTimedPromiseResult>> {
  arr = arr || [];
  const startTime = Date.now();
  return Promise.allSettled(arr.map(o => o.promise)).then(results =>
    results.map((r, i): _settledTimedPromiseResult => {
      const base = { name: arr[i].name, tt: Date.now() - startTime };
      if (r.status === 'fulfilled') {
        return { ...base, status: 'fulfilled', value: r.value };
      }
      return { ...base, status: 'rejected', reason: r.reason };
    })
  );
}
